
import React, { useState } from "react";
import { Editor } from "@tiptap/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlignLeft, AlignCenter, AlignRight, Loader2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

interface ImageFormProps {
  editor: Editor;
  onClose: () => void;
  uploadImage?: (file: File) => Promise<string>;
}

const ImageForm = ({ editor, onClose, uploadImage }: ImageFormProps) => {
  const [url, setUrl] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [altText, setAltText] = useState("");
  const [caption, setCaption] = useState("");
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [alignment, setAlignment] = useState("center");
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      let imageUrl = url;

      if (file && uploadImage) {
        setIsUploading(true);
        console.log("Uploading image file:", file.name);
        try {
          imageUrl = await uploadImage(file);
          console.log("Image upload result URL:", imageUrl);
          
          if (!imageUrl) {
            throw new Error("Upload failed - no URL returned");
          }
        } catch (uploadError: any) {
          console.error("Error during image upload:", uploadError);
          toast({
            variant: "destructive",
            title: "Upload Failed",
            description: uploadError.message || "Failed to upload image"
          });
          setIsUploading(false);
          return;
        }
      }

      if (!imageUrl) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "No image URL provided or upload failed"
        });
        return;
      }

      // Create the image attributes
      const attrs: any = {
        src: imageUrl,
        alt: altText,
        title: caption
      };

      if (width) attrs.width = width;
      if (height) attrs.height = height;

      console.log("Inserting image with attributes:", attrs);
      
      // Insert the image into the editor
      editor.chain().focus().setImage(attrs).run();

      // Add caption as a paragraph if provided
      if (caption) {
        editor.chain().focus().insertContent({
          type: "paragraph",
          content: [{
            type: "text",
            marks: [{ type: "italic" }],
            text: caption
          }],
          attrs: {
            textAlign: alignment
          }
        }).run();
      }

      toast({
        title: "Success",
        description: "Image inserted successfully"
      });
      
      onClose();
    } catch (error: any) {
      console.error("Error inserting image:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to insert image"
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 pt-1">
      <div className="space-y-2">
        <Label htmlFor="image-url">Image URL</Label>
        <Input
          id="image-url"
          type="url"
          placeholder="https://example.com/image.jpg"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          disabled={!!file || isUploading}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="image-file">Or upload file</Label>
        <Input
          id="image-file"
          type="file"
          accept="image/*"
          onChange={(e) => e.target.files && setFile(e.target.files[0])}
          disabled={!!url || isUploading || !uploadImage}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="alt-text">Alt Text (for accessibility)</Label>
        <Input
          id="alt-text"
          type="text"
          placeholder="Describe the image"
          value={altText}
          onChange={(e) => setAltText(e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="caption">Caption</Label>
        <Textarea
          id="caption"
          placeholder="Optional caption"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          rows={2}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="width">Width</Label>
          <Input
            id="width"
            type="text"
            placeholder="e.g., 500px or 100%"
            value={width}
            onChange={(e) => setWidth(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="height">Height</Label>
          <Input
            id="height"
            type="text"
            placeholder="e.g., 300px"
            value={height}
            onChange={(e) => setHeight(e.target.value)}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Alignment</Label>
        <div className="flex space-x-1">
          <Button
            type="button"
            variant={alignment === "left" ? "default" : "outline"}
            size="sm"
            onClick={() => setAlignment("left")}
            className="flex-1"
          >
            <AlignLeft className="h-4 w-4 mr-1" /> Left
          </Button>
          <Button
            type="button"
            variant={alignment === "center" ? "default" : "outline"}
            size="sm"
            onClick={() => setAlignment("center")}
            className="flex-1"
          >
            <AlignCenter className="h-4 w-4 mr-1" /> Center
          </Button>
          <Button
            type="button"
            variant={alignment === "right" ? "default" : "outline"}
            size="sm"
            onClick={() => setAlignment("right")}
            className="flex-1"
          >
            <AlignRight className="h-4 w-4 mr-1" /> Right
          </Button>
        </div>
      </div>

      <Button
        type="submit"
        size="sm"
        disabled={isUploading || (!url && !file)}
        className="w-full"
      >
        {isUploading ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...
          </>
        ) : (
          "Insert Image"
        )}
      </Button>
    </form>
  );
};

export default ImageForm;
